import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { JournalStatus, MoneyValue } from '../api/types';
import { getActiveLocale } from '../i18n/i18n';
import { formatMoneyForDisplay } from '../lib/currencyDisplay';
import { documentTone } from '../lib/subledger';
import { useCurrencyDisplayPolicy } from './CurrencyDisplayProvider';
import { Icon, type IconName } from './Icon';

export function PageHeader({ eyebrow, title, description, actions }: { eyebrow?: string; title: string; description: string; actions?: ReactNode }) {
  return (
    <header className="page-header">
      <div className="page-header__copy">
        {eyebrow && <p className="eyebrow">{eyebrow}</p>}
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      {actions && <div className="page-header__actions">{actions}</div>}
    </header>
  );
}

/**
 * The vendor credit that closes every shell — the public site, the console, the
 * sign-in surfaces, and the platform admin console. It is a company name rather
 * rather than ordinary application copy, while the surrounding phrase follows
 * the active language.
 */
export function PoweredBy() {
  const { t } = useTranslation();
  return (
    <a className="powered-by" dir="auto" href="https://exirsoft.com" target="_blank" rel="noreferrer">
      {t('app.poweredBy')}
    </a>
  );
}

export function Identifier({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <bdi dir="ltr" className={`identifier ${className}`.trim()}>{children}</bdi>;
}

export function Money({ value, compact = false, className = '' }: { value: MoneyValue; compact?: boolean; className?: string }) {
  const policy = useCurrencyDisplayPolicy();
  return <bdi className={`money ${className}`.trim()}>{formatMoneyForDisplay(value, getActiveLocale(), policy, compact)}</bdi>;
}

export function StatusBadge({ status }: { status: JournalStatus | 'closed' | 'active' | 'pending' | 'blocked' }) {
  const { t } = useTranslation();
  return (
    <span className={`status-badge status-badge--${status}`}>
      <span className="status-badge__dot" aria-hidden="true" />
      {t(`status.${status}`)}
    </span>
  );
}

/** Renders any subledger document status (AR/AP/treasury enums) with a tone derived from its name. */
export function DocumentStatus({ status }: { status: string }) {
  const { t } = useTranslation();
  return (
    <span className={`doc-status doc-status--${documentTone(status)}`}>
      <span className="doc-status__dot" aria-hidden="true" />
      {t(`docStatus.${status}`, { defaultValue: status })}
    </span>
  );
}

export function LoadingState() {
  const { t } = useTranslation();
  return (
    <div className="loading-state" role="status" aria-live="polite">
      <span className="spinner" aria-hidden="true" />
      <span>{t('common.loading')}</span>
    </div>
  );
}

export function ErrorState({ retry }: { retry?: () => void }) {
  const { t } = useTranslation();
  return (
    <section className="error-state" role="alert">
      <span className="error-state__icon"><Icon name="alert" /></span>
      <h2>{t('errors.pageTitle')}</h2>
      <p>{t('errors.pageMessage')}</p>
      {retry && <button className="button button--secondary" type="button" onClick={retry}>{t('common.retry')}</button>}
    </section>
  );
}

export function SectionHeading({ title, description, action }: { title: string; description?: string; action?: ReactNode }) {
  return (
    <div className="section-heading">
      <div>
        <h2>{title}</h2>
        {description && <p>{description}</p>}
      </div>
      {action}
    </div>
  );
}

export function EmptyState({ icon = 'search', title, description }: { icon?: IconName; title: string; description: string }) {
  return (
    <div className="empty-state">
      <span className="empty-state__icon"><Icon name={icon} /></span>
      <h3>{title}</h3>
      <p>{description}</p>
    </div>
  );
}
